import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { ReactComponent as ProductHeart } from "./../assets/svgs/product-heart.svg";
import { formatStorePrice, getProductPricing } from "../utils";

const ProductCard = ({
  item,
  imageHeightClass,
  matchPercentage,
  onSaveBoard,
  onRemoveBoard,
  isSavingBoard = false,
  isRemovingBoard = false,
  compactActions = false,
  boardActionMode = "save",
  priorityImage = false,
  hideActions = false,
}) => {
  const [hasImageError, setHasImageError] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const navigate = useNavigate();
  const isRemoveMode = boardActionMode === "remove";
  const isBoardActionLoading = isRemoveMode ? isRemovingBoard : isSavingBoard;
  useEffect(() => {
    setHasImageError(false);
    setIsImageLoaded(false);
  }, [item?.image]);

  const resolvedMatchPercentage =
    item?.matchPercentage ?? matchPercentage ?? null;

  const hasMatchPercentage =
    resolvedMatchPercentage !== null &&
    resolvedMatchPercentage !== undefined &&
    resolvedMatchPercentage !== "";

  const displayPercentage = hasMatchPercentage
    ? `${resolvedMatchPercentage}%`
    : null;

  const rawSlug = item?.real_slug || item?.slug || "";
  const productSlug = String(rawSlug || "").trim();

  const isMarketplaceProductBySlug = productSlug.startsWith("mrk-");

  const productType =
    item?.productType ||
    item?.product_type ||
    (isMarketplaceProductBySlug
      ? "marketplace"
      : item?.is_marketplace === false
        ? "product"
        : "marketplace");

  const isCollectionProduct =
    productType === "product" &&
    !isMarketplaceProductBySlug;

  const pricing = getProductPricing(
    item,
    isCollectionProduct,
  );

  const rawPrice =
    item?.original_price ??
    item?.originalPrice ??
    item?.price;

  const hasPrice =
    rawPrice !== null &&
    rawPrice !== undefined &&
    rawPrice !== "";

  const displayPrice = hasPrice
    ? formatStorePrice(pricing.discountedPrice)
    : "";

  const originalDisplayPrice = hasPrice
    ? formatStorePrice(pricing.originalPrice)
    : "";

  const discountPercentage = Number(
    pricing.discountPercent.toFixed(2),
  );

  const queryParams = new URLSearchParams();

  if (hasMatchPercentage) {
    queryParams.set("matchPercentage", String(resolvedMatchPercentage));
  }

  const productDetailsUrl = productSlug
    ? `/product/${productSlug}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`
    : `/product?productId=${item.id}${
        hasMatchPercentage ? `&matchPercentage=${resolvedMatchPercentage}` : ""
      }`;
  const virtualTryOnUrl = `${productDetailsUrl}${
    productDetailsUrl.includes("?") ? "&" : "?"
  }try_on=true`;

  const detailsState = {
    productId: item.id,
    slug: productSlug,
    productSlug,
    matchPercentage: hasMatchPercentage
      ? Number(resolvedMatchPercentage)
      : null,
    productType,
    product_type: productType,
    is_marketplace: productType === "marketplace",
  };

  const handleVirtualTryOnClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const token = localStorage.getItem("authToken");

    if (!token) {
      toast.error("Please sign in to use this feature.");
      return;
    }

    navigate(virtualTryOnUrl, {
      state: {
        ...detailsState,
        openTryOnModal: true,
      },
    });
  };

  const handleBoardActionClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isRemoveMode) {
      onRemoveBoard?.(item);
      return;
    }

    onSaveBoard?.(item);
  };

  const hasUsableImage = Boolean(item?.image) && item.image !== "null";
  const showFallback = !hasUsableImage || hasImageError;
  const isImageLoading = hasUsableImage && !hasImageError && !isImageLoaded;
  const handleImageLoad = async (event) => {
    const image = event.currentTarget;
    try {
      if (typeof image.decode === "function") await image.decode();
    } catch {}
    setIsImageLoaded(true);
  };

  const handleImageError = () => {
    setHasImageError(true);
    setIsImageLoaded(true);
  };

  return (
    <article className="group mb-0 break-inside-avoid overflow-hidden rounded-[8px] bg-white">
      <Link to={productDetailsUrl} state={detailsState} className="block">
        <div
          className={`relative overflow-hidden rounded-[7px] ${imageHeightClass}`}
        >
          {showFallback ? (
            <div className="flex h-full w-full items-center justify-center bg-[#5F5F5D]">
              <span className="font-inter text-[14px] font-normal text-white/35">
                Product Image
              </span>
            </div>
          ) : (
            <>
              {/* Image loading skeleton */}
              {isImageLoading && (
                <div
                  aria-hidden="true"
                  className="absolute inset-0 z-10 animate-pulse bg-[#ECECEC]"
                />
              )}
              <img
                src={item.image}
                alt={item.name || "Product"}
                loading={priorityImage ? "eager" : "lazy"}
                fetchPriority={priorityImage ? "high" : "auto"}
                decoding="async"
                onLoad={handleImageLoad}
                onError={handleImageError}
                className={[
                  "h-full w-full object-cover",
                  "transition-all duration-300 ease-out",
                  "md:group-hover:scale-[1.03]",
                  "md:group-hover:brightness-[0.72]",
                  isImageLoaded
                    ? "opacity-100"
                    : "opacity-0",
                ].join(" ")}
              />
            </>
          )}

          {/* Desktop hover gradient */}
          {!isImageLoading && (
            <div className="pointer-events-none absolute inset-0 hidden bg-[linear-gradient(180deg,rgba(0,0,0,0.18)_0%,rgba(0,0,0,0.05)_35%,rgba(0,0,0,0.46)_100%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:block" />
          )}

          {/* Collection discount badge */}
          {!isImageLoading && pricing.hasDiscount ? (
            <span className="pointer-events-none absolute left-[8px] top-[8px] z-20 inline-flex min-h-[26px] items-center rounded-[5px] bg-[#EAF7EE] px-2.5 py-1 font-spaceMono text-[10px] font-semibold text-[#257A45] shadow-[0_1px_2px_rgba(0,0,0,0.08)]">
              {discountPercentage}% OFF
            </span>
          ) : null}

          {/* Mobile board action */}
          {!hideActions && !isImageLoading && (
            <button
              type="button"
              aria-label={isRemoveMode ? "Remove product from board" : "Save product"}
              onClick={handleBoardActionClick}
              disabled={isBoardActionLoading}
              className={[
                "absolute right-[8px] top-[8px] z-20 flex h-[30px] min-w-[38px] items-center justify-center rounded-[6px] px-2 text-[12px] md:hidden",
                isRemoveMode
                  ? "bg-white text-[#D43F3F]"
                  : "bg-[#F7DCDC] text-[#D57D7D]",
                isBoardActionLoading ? "cursor-not-allowed opacity-60" : "",
              ].join(" ")}
            >
              {isRemoveMode ? (
                isRemovingBoard ? (
                  "..."
                ) : (
                  "Remove"
                )
              ) : (
                <ProductHeart />
              )}
            </button>
          )}

          {/* Desktop hover actions */}
          {!hideActions && !isImageLoading && (
            <div className="absolute bottom-[8px] left-[8px] right-[8px] hidden flex-col gap-[5px] opacity-0 transition-opacity duration-300 md:flex md:group-hover:opacity-100">
              <button
                type="button"
                onClick={handleVirtualTryOnClick}
                className="flex h-[26px] w-full items-center justify-center rounded-[5px] bg-white px-3 py-[4px] font-mono text-[12px] font-normal text-black shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
              >
                Virtual Try-On
              </button>
              <div className="flex min-w-0 gap-[5px]">
                <span
                  className={`inline-flex h-[26px] min-w-0 flex-1 items-center justify-center truncate rounded-[5px] bg-white py-[4px] font-mono font-normal text-black shadow-[0_1px_2px_rgba(0,0,0,0.08)] ${
                    compactActions ? "px-2 text-[11px]" : "px-[12px] text-[13px]"
                  }`}
                >
                  {displayPercentage
                    ? `${displayPercentage} Match`
                    : "Find out match %"}
                </span>

                <button
                  type="button"
                  disabled={isBoardActionLoading}
                  onClick={handleBoardActionClick}
                  className={[
                    "inline-flex h-[26px] shrink-0 items-center justify-center gap-1 rounded-[5px] py-[4px] font-inter text-[12px] font-normal disabled:cursor-not-allowed disabled:opacity-60",
                    compactActions
                      ? isRemoveMode
                        ? "min-w-[78px] px-2"
                        : "min-w-[64px] px-2"
                      : "min-w-[102px] px-[12px]",
                    isRemoveMode
                      ? "bg-white text-[#D43F3F]"
                      : "bg-[#F7DCDC] text-[#E05F5F]",
                  ].join(" ")}
                >
                  {!isRemoveMode ? (<ProductHeart />) : null}

                  <span>
                    {isRemoveMode
                      ? isRemovingBoard
                        ? "Removing..."
                        : "Remove"
                      : "Save"}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Match percentage shown when not hovering */}
          {!hideActions && !isImageLoading && (
            <span className="absolute bottom-[8px] left-[8px] inline-flex h-[26px] items-center rounded-[5px] bg-white px-[12px] py-[4px] font-mono text-[12px] font-normal text-black shadow-[0_1px_2px_rgba(0,0,0,0.08)] transition-opacity duration-200 md:group-hover:opacity-0">
              {displayPercentage
                ? `${displayPercentage} Match`
                : "Find out match %"}
            </span>
          )}
        </div>

        {/* Product information */}
        {isImageLoading ? (
          <div className="flex animate-pulse items-start justify-between gap-3 px-2 pb-1 pt-2">
            <div className="h-[16px] w-[65%] rounded bg-[#ECECEC]" />
            <div className="h-[16px] w-[20%] rounded bg-[#ECECEC]" />
          </div>
        ) : (
          <div className="flex items-start justify-between gap-3 px-2 pb-1 pt-2">
            <h3 className="m-0 min-w-0 flex-1 truncate font-inter text-[14px] font-medium leading-[20px] text-black">
              {item.name || item.brand || "Product"}
            </h3>
            {displayPrice ? (
              <div className="shrink-0 text-right">
                {pricing.hasDiscount ? (
                  <p className="m-0 font-inter text-[11px] font-normal leading-[14px] text-[#8A8A8A] line-through">
                    {originalDisplayPrice}
                  </p>
                ) : null}
                <p
                  className={[
                    "m-0 font-inter text-[14px] leading-[20px]",
                    pricing.hasDiscount
                      ? "font-semibold text-[#257A45]"
                      : "font-normal text-black",
                  ].join(" ")}
                >
                  {displayPrice}
                </p>
              </div>
            ) : null}
          </div>
        )}
      </Link>
    </article>
  );
};

export default ProductCard;
